// Minimal wrapper around Plausible. Plausible's hosted script exposes
// `window.plausible(eventName, { props })` once the <script> in index.html
// has loaded. This wrapper guards against three real-world cases:
//
//   1. Script not loaded yet (slow network, ad-blocker, dev environment) —
//      `window.plausible` is undefined → silent no-op.
//   2. SSR / build-time → `window` is undefined → silent no-op.
//   3. Domain env var not configured → script never injected, same as #1.
//
// Calling sites can always invoke `trackEvent(...)` without try/catch and
// without worrying whether analytics are wired up in the current
// environment. Worst case: the event is dropped.

/**
 * Records a custom Plausible event.
 * Plausible event names should be human-readable Title Case strings —
 * they show up verbatim in the dashboard. Goals must be pre-defined in
 * the Plausible UI before they appear in conversion reports, but events
 * are recorded regardless.
 *
 * @param {string} name      Event name (e.g. 'Pricing CTA Click')
 * @param {object} [props]   Extra dimensions (e.g. { cycle: 'monthly' })
 */
export function trackEvent(name, props = undefined) {
  if (typeof window === 'undefined') return;
  if (typeof window.plausible !== 'function') return;
  try {
    if (props && Object.keys(props).length > 0) {
      window.plausible(name, { props });
    } else {
      window.plausible(name);
    }
  } catch (err) {
    // Plausible failures must never break the host app — analytics are
    // strictly best-effort. Logged only at debug level to surface the
    // problem during local dev without spamming production consoles.
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[analytics] trackEvent failed:', err);
    }
  }
}

/**
 * Returns true if the Plausible script appears to be active in the
 * current window. Useful for conditionally rendering analytics-related
 * UI ("View stats on Plausible…") without falsely showing it when the
 * script is blocked.
 */
export function isAnalyticsActive() {
  return typeof window !== 'undefined' && typeof window.plausible === 'function';
}

// ── Signup-Attribution ──────────────────────────────────────────────────
// Hält fest, woher eine Anmeldung kam (utm_source der Einstiegs-URL), damit
// die Kanalbewertung bis auf die Conversion-Ebene reicht und nicht bei den
// Pageviews stehen bleibt.
//
// Bewusst OHNE Speicherung im Endgerät (kein localStorage/sessionStorage):
// § 25 TDDDG erlaubt das ohne Einwilligung nur, wenn es für den gewünschten
// Dienst unbedingt nötig ist - eine Kanalstatistik ist das nicht. Der Wert
// lebt deshalb nur im Speicher der geöffneten Seite (überlebt SPA-Navigation
// wie / -> /register, aber keinen Reload). Beim signUp geht er ins Profil
// (profiles.signup_source), offengelegt in der Datenschutzerklärung.
//
// Cross-Domain: die meisten Besucher kommen über die Landing
// (toleranzrechnung.de?utm_source=…). infra/landing/assets/site.js hängt den
// Parameter an die Links an (ebenfalls ohne Speicherung), hier wird er aus
// der App-URL gelesen.

// Altbestand aus der localStorage-Variante (Juni bis Oktober 2026).
const LEGACY_SIGNUP_SOURCE_KEY = 'mk_signup_source';

let signupSource = '';

// Nur unverdächtige Zeichen, hart gekürzt — die DB säubert zusätzlich.
function sanitizeSource(raw) {
  if (!raw) return '';
  return String(raw).replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 60);
}

/**
 * Liest utm_source aus der aktuellen URL (einmalig - der erste Treffer
 * zählt) und räumt den Altbestand im localStorage weg. Idempotent, darf bei
 * jedem App-Start aufgerufen werden. Reines best-effort, wirft nie.
 */
export function captureSignupSource() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(LEGACY_SIGNUP_SOURCE_KEY);
  } catch {
    // localStorage kann blockiert sein (Private Mode) — egal.
  }
  try {
    if (signupSource) return;
    signupSource = sanitizeSource(new URLSearchParams(window.location.search).get('utm_source'));
  } catch {
    // ungültige URL o. ä. — dann eben 'direct'.
  }
}

/**
 * Gibt die Signup-Quelle zurück, oder 'direct' wenn keine bekannt ist. Beim
 * signUp als Metadatum mitzugeben.
 * @returns {string}
 */
export function getSignupSource() {
  return signupSource || 'direct';
}
